import { OnPageLoadOrPjax } from "./app";
import { collectFuzzyFocusSelector, focusableChoicesWithin } from "./collectFuzzyFocusSelector";
import { RegisterStartOfTask, RegisterEndOfTask, RedirectTo } from "./countedFetch";
import { ProgCss } from "./ProgCss.Generated";
import { removeLoadingIndicator, showLoadingIndicator } from "./loaders";
import { cast, ConditionalLog } from "./tools";
import tinymce from "tinymce"
import { safeSessionStorage } from "./safeSessionStorage";

export enum WhoHandlesEvent {
    Undetermined = 0,
    PJax,
    Browser,
}

export function AppState() {
    return document.getElementById("app-state");
}

enum AppContext {
    Pnet = 'Pnet',
    Portaal = 'Portaal',
    Catalogus = 'Catalogus',
}

function ParseAppContext(context: string | null): AppContext | undefined {
    if (context != null && context in AppContext) {
        return AppContext[context as keyof typeof AppContext];
    }

    return undefined;
}

function CurrentAppContext(): AppContext | undefined {
    const appState = AppState();
    if (!appState) {
        return undefined;
    }

    const context = ParseAppContext(appState.getAttribute("data-app-context"));
    if (!context) {
        throw new Error("wrong app javascript mode: " + appState.getAttribute("data-app-context"));
    }

    return context;
}

function rootOrgCode(appState: HTMLElement | null) {
    return appState?.getAttribute("data-rootorg") || location.pathname.split('/')[1];
}

export function getBrowserTabId(appState?: HTMLElement | null) {
    return (appState ?? AppState())?.getAttribute("data-browserTabId");
}

let shouldSaveSession = true;
(window as any).disableSessionTabRestore_forTest = () => {
    safeSessionStorage?.clear();
    shouldSaveSession = false;
}

addEventListener("beforeunload", () => {
    const appState = AppState();
    const rootOrg = rootOrgCode(appState);
    const browserTabId = getBrowserTabId(appState);
    if (shouldSaveSession && rootOrg && browserTabId) {
        ConditionalLog.log("setting aside session " + rootOrg + "/" + browserTabId + " on " + location.href);
        safeSessionStorage?.setItem("browserTabId", browserTabId);
        safeSessionStorage?.setItem("browserTab-rootOrg", rootOrg);
        ConditionalLog.log("done");
    } else {
        ConditionalLog.log("NOT setting aside session " + rootOrg + "/" + browserTabId + " on " + location.href);
    }
});

export function doLoadingPageRequest(currentLocation: URL) {
    const appState = AppState();
    const rootOrg = rootOrgCode(appState);
    const browserTabId_rootOrg = safeSessionStorage?.getItem("browserTab-rootOrg");
    const browserTabId = safeSessionStorage?.getItem("browserTabId");
    safeSessionStorage?.removeItem("browserTabId");
    safeSessionStorage?.removeItem("browserTab-rootOrg");
    const extraAnnotation = location.href !== currentLocation.href ? " (loaded from " + location.href + ")" : "";
    const descriptionForLog = `session ${browserTabId} on ${currentLocation.href}${extraAnnotation}`;
    if (rootOrg && rootOrg === browserTabId_rootOrg && browserTabId) {
        ConditionalLog.log("loading " + descriptionForLog);
        appState?.setAttribute('data-browserTabId', browserTabId);
    } else {
        ConditionalLog.log("ignoring " + descriptionForLog);
    }
    finalizeDoRequest();
    doRequest(currentLocation.href);
}

export type OnPjaxUnmountHandler = () => void;
const PjaxUnmountHandlers: OnPjaxUnmountHandler[] = [];

export function AddPjaxUnmountListener(handler: OnPjaxUnmountHandler) {
    PjaxUnmountHandlers.push(handler);
}
function CallUnmountHandlers() {
    while (PjaxUnmountHandlers.length) {
        PjaxUnmountHandlers.pop()?.();
    }
}

const currentAppJsScriptTags = document.head.querySelectorAll('script[src^="/dist/app.js?v="]');
const currentScriptVersionToken = currentAppJsScriptTags.length == 1
    ? new URL(currentAppJsScriptTags[0].getAttribute("src") ?? "", document.baseURI).searchParams.get("v")
    : null;

let runningPjax = false;
export async function doRequest(
    Href: string,
    Method?: string,
    FormData?: FormData,
    TargetAppContext?: AppContext
) {
    if (!currentScriptVersionToken) {
        throw new Error("Could not find unique version token for current document, aborting.");
    }

    const requestedUri = new URL(Href, document.baseURI);
    const pnetMode = (TargetAppContext ?? CurrentAppContext()) === AppContext.Pnet;

    const body = document.body;
    if (runningPjax) {
        throw new Error("Concurrent Pjax detected; aborting");
    }
    RegisterStartOfTask();
    runningPjax = true;

    let focusSelector = collectFuzzyFocusSelector();
    showLoadingIndicator();
    try {
        const initialAppState = AppState();
        let targetEndpointUri;
        let actualMethod = Method ?? "GET";

        if (pnetMode) {
            targetEndpointUri = new URL(location.origin + "/api/pjax");
            actualMethod = "POST";
            for (const keyValueFromUri of requestedUri.searchParams) {
                targetEndpointUri.searchParams.set(keyValueFromUri[0], keyValueFromUri[1]);
            }
        } else {
            //clone so the searchParams don't affect the original
            targetEndpointUri = new URL(requestedUri);
        }

        targetEndpointUri.searchParams.set("token", initialAppState?.getAttribute("data-token") || "");
        targetEndpointUri.searchParams.set("browserTabId", getBrowserTabId() || "");
        targetEndpointUri.searchParams.set("path", requestedUri.pathname);

        const requestStart = performance.now();
        const response = await fetch(targetEndpointUri, { method: actualMethod, body: FormData });


        const responseStart = performance.now();

        const contentDisposition = response.headers.get("Content-Disposition");
        const dispositionAttachment = contentDisposition && contentDisposition.startsWith("attachment");
        const contentType = response.headers.get('Content-Type');
        const isHtmlResponse = contentType?.startsWith("text/html");
        const shouldParseAsHtml = isHtmlResponse && !dispositionAttachment;
        const blob = await response.blob();

        if (shouldParseAsHtml) {
            const text = await blob.text();
            const responseEnd = performance.now();

            const parsedResponse = new DOMParser().parseFromString(text, "text/html");
            const appState = parsedResponse.getElementById("app-state");

            if (appState) {
                const version = appState.getAttribute("data-version") ?? "";
                if (version != currentScriptVersionToken) {
                    ConditionalLog.log("version mismatch (refreshing):", version, currentScriptVersionToken);
                    RedirectTo(requestedUri.href); //reattempt to go to intended href
                    return;
                }

                maintainExistingBrowserTabIdOnErrorPages(appState, initialAppState);

                CallUnmountHandlers();
                body.replaceChildren(...parsedResponse.body.childNodes);

                const domComplete = performance.now();

                focusSelector.tryToRestoreFocus();

                const elementToScrollTo = requestedUri.hash && document.querySelector(requestedUri.hash);
                if (elementToScrollTo) {
                    elementToScrollTo.scrollIntoView();
                }

                OnPageLoadOrPjax({
                    perf: { requestStart, responseStart, responseEnd, domComplete, },
                    location: response.redirected && response.url ? new URL(response.url) : requestedUri,
                    allowPushState: pnetMode || response.redirected || (actualMethod === "GET"),
                });
            } else {
                if (document.documentElement.getAttribute("data-niet-productie-omgeving")) {
                    console.error("contentType is text/html, but no app-state was found!");
                }
                const iframe = document.createElement("iframe");
                iframe.src = URL.createObjectURL(blob);
                iframe.style.cssText = "position: fixed; inset: 0; border-style: none; width: 100vw; height: 100vh";

                CallUnmountHandlers();
                body.replaceChildren(iframe);
                syncHistoryWithNewUrl(requestedUri.href, actualMethod === "GET");
            }
        } else {
            const blobUrl = URL.createObjectURL(blob);
            const filename = extractFilename(contentDisposition) ?? "download";
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.style.display = 'none';

            body.appendChild(a);
            a.click();
            URL.revokeObjectURL(blobUrl);
            body.removeChild(a);

            removeLoadingIndicator();
        }
    } finally {
        finalizeDoRequest();
    }
}

function finalizeDoRequest() {
    if (runningPjax) {
        runningPjax = false;
        RegisterEndOfTask();
    }
}


function maintainExistingBrowserTabIdOnErrorPages(appState: HTMLElement, previousAppState: HTMLElement | null) {
    //some pages fail to fully fill appState. When this is the case, but the previous appState was completely filled,
    //then we should maintain the tab-id for a short while so that future page-loads might continue the session.

    const newBrowserTabId = getBrowserTabId(appState);
    const oldBrowserTabId = getBrowserTabId(previousAppState);
    const newOrg = rootOrgCode(appState);
    const oldOrg = rootOrgCode(previousAppState);

    if (!newBrowserTabId && oldBrowserTabId && oldOrg && (!newOrg || newOrg === oldOrg)) {
        ConditionalLog.log("maintaining session " + oldOrg + "/" + oldBrowserTabId);
        appState.setAttribute("data-rootorg", oldOrg);
        appState.setAttribute('data-browserTabId', oldBrowserTabId);
    }
}

function extractFilename(contentDisposition: string | null) {
    if (!contentDisposition || !contentDisposition.includes("attachment")) {
        return null;
    }

    const utf8Pattern = /filename\*=UTF-8''([^;\n\r]+)/i;
    const utf8Match = contentDisposition.match(utf8Pattern);

    if (utf8Match) {
        return decodeURIComponent(utf8Match[1]);
    }

    const regularPattern = /filename=(\"|')?([^;\"'\n\r]+)(\"|')?/i;
    const regularMatch = contentDisposition.match(regularPattern);

    if (regularMatch) {
        return regularMatch[2];
    }

    return null;
}

(window as any).extractFilename_ForTest = extractFilename;

async function formSubmit(form: HTMLFormElement, additionalName?: string | null, additionalValue?: string | null) {
    tinymce.triggerSave();

    const data = new FormData(form);

    if (form.method.toLowerCase() == "post") {
        if (additionalName) {
            data.append(additionalName, additionalValue || "");
        }

        await doRequest(form.getAttribute("action") ?? location.href, form.method, data);
        return;
    }

    const uri = new URL(form.action);

    for (const pair of data.entries()) {
        uri.searchParams.set(pair[0], pair[1].toString());
    }

    if (additionalName) {
        uri.searchParams.set(additionalName, additionalValue || "");
    }

    await doRequest(uri.toString(), form.method, undefined);
};

export function tryAsSubmitButton(target: Node): WhoHandlesEvent {
    if ((target instanceof HTMLButtonElement || target instanceof HTMLInputElement) && target.form != null) {
        if (target.type === "submit") {
            target.form.requestSubmit(target);
            return WhoHandlesEvent.PJax;
        }
    }
    return WhoHandlesEvent.Undetermined;
}

export function tryAsLinkClick(target: Node, ctrlKey: boolean): WhoHandlesEvent {
    if (target instanceof HTMLAnchorElement && target.href) {
        const href = target.getAttribute("href");
        const anchorIndex = href?.indexOf("#");
        const hasAnchor = anchorIndex != undefined && anchorIndex > -1;
        if (target.host !== location.host || target.hasAttribute("data-no-pjax") || target.hasAttribute("download") || target.hasAttribute("target") || ctrlKey || hasAnchor) {
            return WhoHandlesEvent.Browser;
        }

        const appContext = ParseAppContext(target.getAttribute("data-app-context"));
        doRequest(href ?? location.href, undefined, undefined, appContext);
        return WhoHandlesEvent.PJax;
    }
    return WhoHandlesEvent.Undetermined;
}

const multiSelectState = new WeakMap<HTMLElement, { lastRowIndex: number; wasAdd: boolean }>();

function postRowToggle(
    tbody: HTMLElement,
    rowIndex: string,
    currentRowIndex: number,
    willBeAdded: boolean,
    shiftKey: boolean | undefined,
): WhoHandlesEvent {
    const prevState = multiSelectState.get(tbody);
    if (shiftKey && prevState) {
        const rangeCallbackID = tbody.getAttribute("data-onrowtogglerange");
        if (rangeCallbackID) {
            const formData = new FormData();
            formData.append(rangeCallbackID, prevState.lastRowIndex.toString());
            formData.append(rangeCallbackID, rowIndex);
            formData.append(rangeCallbackID, prevState.wasAdd ? "add" : "remove");
            doRequest(location.href, "POST", formData);
            return WhoHandlesEvent.PJax;
        }
    }
    multiSelectState.set(tbody, { lastRowIndex: currentRowIndex, wasAdd: willBeAdded });

    const toggleCallbackID = tbody.getAttribute("data-onrowtoggle");
    if (!toggleCallbackID) {
        return WhoHandlesEvent.Undetermined;
    }
    const formData = new FormData();
    formData.append(toggleCallbackID, rowIndex);
    doRequest(location.href, "POST", formData);
    return WhoHandlesEvent.PJax;
}

export function tryAsMultiSelectRow(target: Node, _ctrlKey?: boolean, shiftKey?: boolean): WhoHandlesEvent {
    if (!(target instanceof HTMLInputElement)) {
        return WhoHandlesEvent.Undetermined;
    }
    if (!target.classList.contains(ProgCss.LijstContainer_checkbox)) {
        return WhoHandlesEvent.Undetermined;
    }
    const tableRow = target.closest("tr");
    if (!tableRow) {
        return WhoHandlesEvent.Undetermined;
    }
    const rowIndex = tableRow.getAttribute("data-rowindex");
    if (!rowIndex) {
        return WhoHandlesEvent.Undetermined;
    }
    const tbody = tableRow.closest("tbody");
    if (!tbody) {
        return WhoHandlesEvent.Undetermined;
    }

    const currentRowIndex = parseInt(rowIndex, 10);

    if (tbody.hasAttribute("data-onrowtoggle")) {
        //target.checked reflects the post-click state.
        return postRowToggle(tbody, rowIndex, currentRowIndex, target.checked, shiftKey);
    }

    const multiselectCallbackID = tbody.getAttribute("data-onmultiselect");
    const formData = new FormData();
    formData.append(multiselectCallbackID!, rowIndex);
    doRequest(location.href, "POST", formData);
    return WhoHandlesEvent.PJax;
}

export function tryAsRowSelect(target: Node, _ctrlKey?: boolean, shiftKey?: boolean): WhoHandlesEvent {
    if (!(target instanceof HTMLTableCellElement)) {
        return WhoHandlesEvent.Undetermined;
    }
    const tableRow = target.closest(`tr.${ProgCss.LijstContainer_row}`);
    if (!tableRow) {
        return WhoHandlesEvent.Undetermined;
    }
    const rowIndex = tableRow.getAttribute("data-rowindex");
    if (!rowIndex) {
        return WhoHandlesEvent.Undetermined;
    }
    const tbody = tableRow.closest("tbody");
    if (!tbody) {
        return WhoHandlesEvent.Undetermined;
    }
    if (target.closest("a:link, a:visited")) {
        return WhoHandlesEvent.Undetermined;
    }
    const focusOpts = { focusVisible: false, preventScroll: true, };
    focusableChoicesWithin(tableRow)[0]?.focus(focusOpts);

    const currentRowIndex = parseInt(rowIndex, 10);

    if (tbody.hasAttribute("data-onrowtoggle")) {
        const willBeAdded = !tableRow.classList.contains(ProgCss.is_selected);
        return postRowToggle(tbody, rowIndex, currentRowIndex, willBeAdded, shiftKey);
    }

    const singleselectCallbackID = tbody.getAttribute("data-onsingleselect");
    const formData = new FormData();
    formData.append(singleselectCallbackID!, rowIndex);
    doRequest(location.href, "POST", formData);
    return WhoHandlesEvent.PJax;
}

export function syncHistoryWithNewUrl(newPathName: string, allowPushState: boolean) {
    if (lastWindowLocation === null) {
        history.replaceState({}, "", newPathName);
    } else if (newPathName !== lastWindowLocation && allowPushState) {
        history.pushState({}, "", newPathName);
    }

    lastWindowLocation = newPathName;
}

function shouldRefresh(lastWindowLocation: string | null): boolean {
    if (lastWindowLocation !== null) {
        const lastWithoutHash = new URL(lastWindowLocation, document.baseURI);
        lastWithoutHash.hash = '';
        const currWithoutHash = new URL(window.location.href);
        currWithoutHash.hash = '';
        if (currWithoutHash.toString() !== lastWithoutHash.toString()) {
            return true;
        }
    }

    return false;
}

export function onPopState() {
    if (shouldRefresh(lastWindowLocation)) {
        lastWindowLocation = null; // to make sure history sync works properly
        doRequest(location.href);
    }
}

let lastWindowLocation: string | null = null;

export function InitPjax() {
    let appContext = CurrentAppContext();
    if (appContext !== AppContext.Pnet && appContext !== AppContext.Portaal) {
        return;
    }

    window.addEventListener("popstate", onPopState);

    document.addEventListener("submit", evt => {
        const form = cast(evt.target, HTMLFormElement);
        if (!evt.submitter) {
            evt.preventDefault();
            formSubmit(form);
            return;
        }

        if ((evt.submitter instanceof HTMLButtonElement || evt.submitter instanceof HTMLInputElement) && evt.submitter.form != null) {
            if (evt.submitter.hasAttribute("data-no-pjax") || evt.submitter.form.hasAttribute("data-no-pjax")) {
                return;
            }

            const formAction = evt.submitter.getAttribute("formaction");
            if (formAction) {
                evt.submitter.form.setAttribute("action", formAction);
            }

            evt.preventDefault();

            formSubmit(evt.submitter.form, evt.submitter.name, evt.submitter.value);
        }
    });

    document.addEventListener(
        "click",
        e => {
            if (e.button === 0 && !e.defaultPrevented) {
                const determineWhoHandlesEvent: ((target: Node, ctrlKey: boolean, shiftKey?: boolean) => WhoHandlesEvent)[] = [
                    tryAsLinkClick,
                    tryAsSubmitButton,
                    tryAsMultiSelectRow,
                    tryAsRowSelect
                ];

                for (let target = e.target; target instanceof Node; target = target.parentElement) {
                    let whoHandlesEvent = WhoHandlesEvent.Undetermined;

                    for (let index = 0; index < determineWhoHandlesEvent.length; index++) {
                        whoHandlesEvent = determineWhoHandlesEvent[index](target, e.ctrlKey, e.shiftKey);
                        if (whoHandlesEvent) {
                            break;
                        }
                    }

                    if (whoHandlesEvent !== WhoHandlesEvent.Undetermined) {
                        if (whoHandlesEvent === WhoHandlesEvent.PJax) {
                            e.preventDefault();
                        }
                        break;
                    }
                }
            }
        });
}
